/*
 *
 * @providesModule InterstitialModalUtils
 *
 */

import {IInterstitialProps} from '@skillz/ui';
import {PaymentInfoKind} from 'StoreInfo';
import CheckoutController from 'CheckoutController';
import StoreFrontStore from 'StoreFrontStore';
import AWSManager from 'AWSManager';
import {INTERSTITIAL_CLAIMED} from 'AWSConstants';
import InterstitialsManager from 'InterstitialsManager';

// TODO: Add these props to IInterstitialProps definition in skillz/ui repo
export interface InterstitialProps extends IInterstitialProps {
  hero_img_url: string;
  banner_img_url?: string;
  priority: number;

  deeplink_ios: string | null; // These properties may be null depending on server configuration
  deeplink_android: string | null;
  redemptions_allowed: number | null;

  /** Interstitial type to handle different behavior across the interstitials system.
   * - DEFAULT: Regular interstitial
   * - HOUSE_AD: House ad interstitial - special handling for house ads. Used to filter out non-house ads
   *  from the interstitials pop-ups when the user is on the old interstitials system (interstitials.enabled = false).
   * - NO_OFFER_TEXT_LTO: LTO interstitial with no offer text. Used to hide the sales content on the LTO interstitials.
   * - DAILY_BONUS: Daily Bonus LTO - offers instant bonus on deposit plus daily bonuses over multiple days.
   */
  interstitial_type: 'DEFAULT' | 'HOUSE_AD' | 'NO_OFFER_TEXT_LTO' | 'DAILY_BONUS';

  /** Optional. When not empty, checkout screen will disable the payment methods not in the list */
  payment_methods?: EInterstitialAllowedPaymentMethod[];
  ticketz?: number;
  points?: number; // Points represent Z currency
  carousel_img_url?: string;

  // Daily Bonus LTO fields
  /** User interstitial state for Daily Bonus LTO */
  user_interstitial_state?: 'AVAILABLE' | 'ACTIVE' | 'ONGOING' | 'CLAIMED' | 'EXPIRED' | 'CLAIM_ACTIVE';
  
  /** Total deferred bonus across all days (from triggered API) - API response field (snake_case) */
  deferred_bonus_cash?: number | null;
  
  /** Total deferred bonus across all days (from triggered API) - mapped to camelCase */
  deferredBonus?: number;
  
  /** Number of days for daily rewards (from triggered API) - API response field (snake_case) */
  daily_reward_duration_days?: number | null;
  
  /** Number of days for daily rewards (from triggered API) - mapped to camelCase */
  dailyRewardDurationDays?: number;
  
  /** Highlight strategy for banner animation */
  highlightStartegy?: 'SHIMMER' | 'NONE';
  
  /** Deposit banner URL for Daily Bonus LTO */
  deposit_banner_img_url?: string;
  
  /** Daily reward hero URL for ONGOING state */
  daily_reward_hero_url?: string;
  
  /** Daily reward metadata (present when ONGOING state) */
  daily_reward_metadata?: {
    day: number;
    currency: string; // "BONUS_CASH" | "TICKETZ" | "POINTS"
    reward_amount: number | string;
    claim_state: 'AVAILABLE' | 'CLAIMED' | 'MISSED';
    claim_expiry_time?: string; // ISO date string
    daily_banner_img_url?: string;
    viewed?: boolean;
  };
  
  /** Daily reward info - when present, banner should use daily_claim_banner_img_url and open calendar summary */
  daily_reward_info?: any | null;
  
  /** Daily claim banner image URL - used when daily_reward_info is present */
  daily_claim_banner_img_url?: string | null;
  user_interstitial_id?: number;
}

export enum EInterstitialAllowedPaymentMethod {
  BANK_ACCOUNT = 'BANK_ACCOUNT',
  CREDIT_CARD = 'CREDIT_CARD',
  PAYPAL = 'PAYPAL',
  APPLE_PAY = 'APPLE_PAY',
  VENMO = 'VENMO',
}

export const PaymentMethodToPaymentInfoKindsMap: Record<
  EInterstitialAllowedPaymentMethod,
  PaymentInfoKind[]
> = {
  [EInterstitialAllowedPaymentMethod.BANK_ACCOUNT]: ['Aeropay', 'SavedBankAccount'],
  [EInterstitialAllowedPaymentMethod.CREDIT_CARD]: ['NewCreditCard', 'SavedCreditCard'],
  [EInterstitialAllowedPaymentMethod.PAYPAL]: ['PayPal'],
  [EInterstitialAllowedPaymentMethod.APPLE_PAY]: ['ApplePay'],
  [EInterstitialAllowedPaymentMethod.VENMO]: ['Venmo'],
};

export const PaymentInfoKindToPaymentMethodMap: Record<
  PaymentInfoKind,
  EInterstitialAllowedPaymentMethod
> = {
  Aeropay: EInterstitialAllowedPaymentMethod.BANK_ACCOUNT,
  SavedBankAccount: EInterstitialAllowedPaymentMethod.BANK_ACCOUNT,
  NewCreditCard: EInterstitialAllowedPaymentMethod.CREDIT_CARD,
  SavedCreditCard: EInterstitialAllowedPaymentMethod.CREDIT_CARD,
  PayPal: EInterstitialAllowedPaymentMethod.PAYPAL,
  ApplePay: EInterstitialAllowedPaymentMethod.APPLE_PAY,
  Venmo: EInterstitialAllowedPaymentMethod.VENMO,
  LinkACHBankPromo: undefined as unknown as EInterstitialAllowedPaymentMethod, // this is not a payment method
};

export const mapAllowedPaymentMethodsToPaymentInfoKind = (
  allowedPaymentMethods: EInterstitialAllowedPaymentMethod[]
): PaymentInfoKind[] => {
  return allowedPaymentMethods.reduce((acc: PaymentInfoKind[], paymentMethod) => {
    return [...acc, ...PaymentMethodToPaymentInfoKindsMap[paymentMethod]];
  }, []);
};

export const isAllInfoKindsFromAllowedPaymentMethod = (
  infoKinds: PaymentInfoKind[],
  paymentMethod: EInterstitialAllowedPaymentMethod
) => {
  return infoKinds.every(
    (infoKind) => PaymentInfoKindToPaymentMethodMap[infoKind] === paymentMethod
  );
};

/**
 * Launches checkout flow for an interstitial/LTO offer.
 * This centralizes the checkout logic used by BannerCard, InterstitialModal, and useLTOCards.
 *
 * @param data - The interstitial data containing offer details
 * @param onCheckoutDisplayed - Optional callback when checkout is displayed (e.g., to close modal)
 */
export const launchInterstitialCheckout = async (
  data: InterstitialProps,
  onCheckoutDisplayed?: () => void,
) => {
  if (CheckoutController.isLaunchingCheckout()) {
    return;
  }

  const hasValidOffer =
    !!data?.id &&
    (data.deposit_amount > 0 || data.bonus_cash > 0 || data.ticketz > 0 || data.points > 0);

  if (!hasValidOffer) {
    // If no valid offer, show interstitial modal instead
    InterstitialsManager.showInterstitial(data);
    return;
  }

  // Track interstitial claimed event
  AWSManager.trackEvent(INTERSTITIAL_CLAIMED, {
    category: 'LTO',
    id: data?.id,
  });

  // Create offer from interstitial data
  const offer = {
    id: String(data.id),
    amount: data.deposit_amount || 0,
    bonus: data.bonus_cash || 0,
    best_value: false,
    is_interstitial_purchase: true,
    ticketz: data.ticketz || 0,
    points: data.points || 0,
    interstitial_type: data.interstitial_type,
  };

  // Fetch storeInfo and launch checkout

  StoreFrontStore.fetchDepositOffersWithCompletion(true, (storeInfo: any) => {
    const isAchOnlyDeposit =
      data.payment_methods?.length === 1 &&
      data.payment_methods?.includes(EInterstitialAllowedPaymentMethod.BANK_ACCOUNT);

    storeInfo.isACHOnlyDeposit = isAchOnlyDeposit;

    CheckoutController.launchCheckoutFlow({
      offer,
      storeInfo,
      verifyEligibilityDuringCheckout: true,
      onCheckoutDisplayed,
      allowedPaymentInfoKinds: mapAllowedPaymentMethodsToPaymentInfoKind(
        data.payment_methods ?? []
      ),
      isACHOnlyDeposit: isAchOnlyDeposit,
      deposit_banner_img_url: data?.deposit_banner_img_url,
    });
  });
};
